/* eslint-disable */
/**
 * Əmək Qabiliyyətinin Müvəqqəti İtirilməsinə Görə (Xəstəlik Vərəqəsi) Müavinət Kalkulyatoru (2026)
 * Əsas Spesifikasiya: Revision 3 (Final Approved) + QA Test Suite (TC-00 - TC-26)
 * 
 * Hüquqi Baza:
 * - AR Əmək Məcəlləsi (maddə 131)
 * - "Sosial sığorta haqqında" AR Qanunu (maddə 14, 15)
 * - AR Vergi Məcəlləsi (maddə 98, 101.1-1, 102.1.4, 102.1.6)
 * - "İcbari tibbi sığorta haqqında" AR Qanunu (maddə 15-10)
 * - "İşsizlikdən sığorta haqqında" AR Qanunu (maddə 9)
 * - AR Nazirlər Kabinetinin 189 nömrəli Qərarı ilə təsdiq edilmiş Qaydanın 1.3, 1.4, 1.5, 2.2, 2.5, 2.9, 2.10.1, 2.10.2, 2.11, 2.17 və 3.3-cü bəndləri
 * - AR ƏƏSMN-in 01.05.2026-cı il tətbiqinə dair rəsmi metodiki izahı
 * - AR DVX-nin 21.01.2026 və 08.09.2026 tarixli rəsmi inzibati izahları
 */

export const STATUTORY_BASE_PARAMS = {
  TRANSITION_DATE: '2026-05-01',
  MINIMUM_PENSION_2026: 320.0,
  PENSION_CAP_MULTIPLIER: 25, // 25 * 320 = 8 000 AZN (Bənd 2.10.2 / 3.3)
  SALARY_CAP_MULTIPLIER: 2.0, // 2x vəzifə maaşı (Bənd 2.10.1)
  MINIMUM_WAGE_2026: 400.0,   // Bənd 2.9 minimum əməkhaqqı fallback
  EMPLOYER_PAID_DAYS: 14,     // İlk 14 təqvim günü (Bənd 2.5)
  MINIMUM_SENIORITY_MONTHS: 6,// Bənd 1.5
  MAX_BENEFIT_DAYS_PER_YEAR: 365, // Bənd 1.4 (Maksimum 1 il)
  TAX_FREE_ALLOWANCE_VM_102_1_6: 200.0,
};

/**
 * 1. Tarixdən əvvəlki ardıcıl 4 rübü təyin edir
 */
export function getPrecedingFourQuarters(eventDateStr) {
  const d = new Date(eventDateStr);
  const year = d.getFullYear();
  const month = d.getMonth() + 1; // 1-12
  const currentQuarter = Math.ceil(month / 3);

  const quarters = [];
  let q = currentQuarter - 1;
  let y = year;

  for (let i = 0; i < 4; i++) {
    if (q === 0) {
      q = 4;
      y -= 1;
    }
    const quarterNumber = q;
    const quarterYear = y;

    let startM, endM, daysCount;
    if (quarterNumber === 1) {
      startM = `${quarterYear}-01-01`;
      endM = `${quarterYear}-03-31`;
      const isLeap = (quarterYear % 4 === 0 && quarterYear % 100 !== 0) || quarterYear % 400 === 0;
      daysCount = isLeap ? 91 : 90;
    } else if (quarterNumber === 2) {
      startM = `${quarterYear}-04-01`;
      endM = `${quarterYear}-06-30`;
      daysCount = 91;
    } else if (quarterNumber === 3) {
      startM = `${quarterYear}-07-01`;
      endM = `${quarterYear}-09-30`;
      daysCount = 92;
    } else {
      startM = `${quarterYear}-10-01`;
      endM = `${quarterYear}-12-31`;
      daysCount = 92;
    }

    quarters.unshift({
      key: `${quarterYear}_Q${quarterNumber}`,
      year: quarterYear,
      quarter: quarterNumber,
      label: `${quarterYear}-ci il ${quarterNumber}-ci rüb`,
      startDate: startM,
      endDate: endM,
      calendarDays: daysCount,
    });
    q -= 1;
  }

  const basePeriodStart = quarters[0].startDate;
  const basePeriodEnd = quarters[quarters.length - 1].endDate;
  const totalBaseDays = quarters.reduce((sum, item) => sum + item.calendarDays, 0);

  return { quarters, basePeriodStart, basePeriodEnd, totalBaseDays };
}

/**
 * 2. Sosial sığorta stajını yoxlayır
 */
export function checkSickLeaveEligibility({
  eventStartDate,
  seniorityYears = 0,
  seniorityMonths = 0,
  seniorityDays = 0,
  caseType = 'ORDINARY_ILLNESS', // 'ORDINARY_ILLNESS' | 'OCCUPATIONAL_ACCIDENT_OR_DISEASE'
  employerFaultStatus = 'UNKNOWN', // 'UNKNOWN' | 'CONFIRMED' | 'NOT_CONFIRMED'
}) {
  const isLegacy = eventStartDate < STATUTORY_BASE_PARAMS.TRANSITION_DATE;
  const totalMonths = Number(seniorityYears) * 12 + Number(seniorityMonths) + Number(seniorityDays) / 30.0;

  if (isLegacy) {
    // Köhnə rejim (01.05.2026-dan əvvəl): 60% / 80% / 100%
    let rate = 0.6;
    let label = 'Staj 8 ilədək (60% ödəniş)';
    if (Number(seniorityYears) >= 12) {
      rate = 1.0;
      label = 'Staj 12 ildən çox (100% ödəniş)';
    } else if (Number(seniorityYears) >= 8) {
      rate = 0.8;
      label = 'Staj 8 ildən 12 ilədək (80% ödəniş)';
    }
    return {
      isLegacy: true,
      eligible: true,
      benefitStatus: 'ELIGIBLE_LEGACY',
      rate,
      rateLabel: label,
      statusMessage: `Köhnə Qayda: Hadisə 01.05.2026-dan əvvəl baş vermişdir. Əmək stajı üzrə ${rate * 100}% tətbiq olunur.`,
    };
  }

  // Yeni rejim (01.05.2026 və sonra):
  const isOccupational = caseType === 'OCCUPATIONAL_ACCIDENT_OR_DISEASE';

  if (totalMonths < STATUTORY_BASE_PARAMS.MINIMUM_SENIORITY_MONTHS) {
    return {
      isLegacy: false,
      eligible: false,
      benefitStatus: 'NOT_ELIGIBLE',
      rate: 0.0,
      rateLabel: '0% (Müavinət hüququ yaranmır)',
      statusMessage: 'Müavinət hüququ yaranmır. Sosial sığorta stajı 6 aydan azdır (Qayda 1.5).',
      isOccupational,
      employerFaultStatus,
    };
  }

  return {
    isLegacy: false,
    eligible: true,
    benefitStatus: 'ELIGIBLE',
    rate: 1.0,
    rateLabel: 'Hüquq yarandıqda müavinət 100% miqdarında hesablanır',
    statusMessage: isOccupational
      ? 'İstehsalat qəzası/peşə xəstəliyi müəyyən edilib. Sığortaedənin təqsir statusu ayrıca İZ aktı ilə müəyyən edilir.'
      : 'Sosial sığorta stajı ən azı 6 aydır. Müavinət 100% miqdarında hesablanır (Qayda 1.5).',
    isOccupational,
    employerFaultStatus,
  };
}

/**
 * 3. 2026 Gəlir Vergisi Hesablanması (VM 101.1-1 və 102.1.6)
 */
export function calculateIncomeTax2026(grossAmount, apply200Allowance = true) {
  const taxable = apply200Allowance
    ? Math.max(0, grossAmount - STATUTORY_BASE_PARAMS.TAX_FREE_ALLOWANCE_VM_102_1_6)
    : Math.max(0, grossAmount);

  if (taxable <= 0) return 0.0;

  let tax = 0;
  if (taxable <= 2500) {
    tax = taxable * 0.03;
  } else if (taxable <= 8000) {
    tax = 75 + (taxable - 2500) * 0.1;
  } else {
    tax = 625 + (taxable - 8000) * 0.14;
  }

  return Number(tax.toFixed(2));
}

/**
 * 4. Xəstəlik vərəqəsi günlərinin təqvim ayları üzrə bölünməsi
 */
export function splitSickLeaveDaysByMonth(startDateStr, endDateStr) {
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);

  const monthMap = {};
  const cur = new Date(start);

  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const monthKey = `${y}-${m}`;

    if (!monthMap[monthKey]) {
      monthMap[monthKey] = {
        monthKey,
        year: y,
        month: cur.getMonth() + 1,
        totalCalendarDaysInMonth: new Date(y, cur.getMonth() + 1, 0).getDate(),
        sickDaysInMonth: 0,
      };
    }
    monthMap[monthKey].sickDaysInMonth += 1;
    cur.setDate(cur.getDate() + 1);
  }

  return Object.values(monthMap);
}

/**
 * 5. Əsas Xəstəlik Müavinəti Hesablama Mühərriki
 */
export function calculateSickLeaveBenefit2026(input) {
  const {
    eventStartDate = '2026-09-24',
    eventEndDate = '2026-10-10',
    seniorityYears = 2,
    seniorityMonths = 0,
    seniorityDays = 0,
    caseType = 'ORDINARY_ILLNESS', // 'ORDINARY_ILLNESS' | 'OCCUPATIONAL_ACCIDENT_OR_DISEASE'
    employerFaultStatus = 'UNKNOWN', // 'UNKNOWN' | 'CONFIRMED' | 'NOT_CONFIRMED'
    monthlySalary = 1500.0,
    // 4 Rüb Qazancı (MDSS hesablanan leqal qazanc)
    quarterlyEarnings = {},
    excludedDaysList = [], // Çıxılan günlər: [{ type, days }]
    // Fasiləsizlik / əvvəlki bülleten
    isContinuation = false,
    previouslyPaidEmployerDays = 0, // Fasiləsiz davamdırsa, əvvəlki bülletendə işəgötürənin ödədiyi günlər
    // Əsas / əlavə iş yeri
    isAdditionalJob = false,
    additionalJobMonthlySalary = 0,
    // Parametrik dəyişənlər
    minimumPension = STATUTORY_BASE_PARAMS.MINIMUM_PENSION_2026,
    minimumWage = STATUTORY_BASE_PARAMS.MINIMUM_WAGE_2026,
    // Layer 2 Payroll Rekonsilyasiyası üçün aylıq digər gəlirlər
    monthlyOtherIncomes = {}, // { '2026-09': { salary: 1400, vacation: 500 }, '2026-10': { salary: 2500, vacation: 0 } }
  } = input;

  // A. Staj və Hüquq Yoxlanması (Qayda 1.5)
  const eligibility = checkSickLeaveEligibility({
    eventStartDate,
    seniorityYears,
    seniorityMonths,
    seniorityDays,
    caseType,
    employerFaultStatus,
  });

  // B. Xəstəlik Dövrü Təqvim Günləri (TGS)
  const startD = new Date(eventStartDate);
  const endD = new Date(eventEndDate);
  const diffTime = Math.abs(endD - startD);
  const TGS = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

  // 1 İllik Limit Yoxlanışı (Qayda bənd 1.4)
  const isMaxPeriodExceeded = TGS > STATUTORY_BASE_PARAMS.MAX_BENEFIT_DAYS_PER_YEAR;
  const maxPeriodWarning = isMaxPeriodExceeded
    ? 'Qayda bənd 1.4: Müavinət əmək qabiliyyəti bərpa olunanadək və ya TSEK tərəfindən əlillik müəyyən edilənədək, lakin bir ildən çox olmayan dövr üçün verilir (365 təqvim günü həddi aşılmışdır).'
    : null;

  // Hüquqi Bildirişlər Paketi
  const legalNotices = {
    lcArticle131Notice: 'Əmək Məcəlləsinin 131-ci maddəsi: Əmək məzuniyyəti dövründə əmək qabiliyyəti müvəqqəti itirildikdə, məzuniyyət xəstəlik vərəqəsində göstərilən günlərin sayına uyğun olaraq uzadılır və ya tərəflərin razılığı ilə başqa vaxta keçirilir.',
    electronicSickLeaveNotice: 'AR NK 189 nömrəli Qərarı bənd 1.3: Müavinət tibb müəssisəsinin elektron formada tərtib etdiyi və Nazirliyin mərkəzləşdirilmiş elektron informasiya sisteminə (MEİS) ötürdüyü elektron xəstəlik vərəqəsi məlumatlarına əsasən təyin edilir.',
    dsmfTaxAgentNotice: 'AR VM 102.1.4 və DVX inzibati izahı: Xəstəlik müavinətindən (həm işəgötürən, həm DSMF hissəsi) gəlir vergisi tutulur. Müavinətdən MDSS, İTS və İŞS tutulmur (0%).',
    taxSimulationDisclaimer: 'Bu hesablama yalnız təxmini vergi rekonsilyasiyası (simulyasiyası) xarakteri daşıyır və mühasibat proqramının rəsmi əməkhaqqı bordrosunu və ya vergi bəyannaməsini birbaşa əvəz etmir.',
    maxPeriodWarning,
  };

  // C. Əgər müavinət hüququ yoxdursa (Staj < 6 ay)
  if (!eligibility.eligible) {
    return {
      inputs: input,
      eligibility,
      TGS,
      isMaxPeriodExceeded,
      legalNotices,
      isClause2_9Applied: false,
      grossBenefit: 0.0,
      employerShareGross: 0.0,
      dsmfShareGross: 0.0,
      cappedGrossBenefit: 0.0,
      dailyRateOQM: 0.0,
      BTG: 0,
      totalEarnings4Quarters: 0.0,
      monthlyBreakdown: [],
      taxSimulation: {
        totalNet: 0.0,
        totalTax: 0.0,
        months: [],
        legalAdvisoryNotice: legalNotices.taxSimulationDisclaimer,
      },
    };
  }

  // D. 4 Rüb və Baza Təyini
  const quarterInfo = getPrecedingFourQuarters(eventStartDate);
  let totalEarnings4Quarters = 0;
  quarterInfo.quarters.forEach((q) => {
    const earn = parseFloat(quarterlyEarnings[q.key]) || 0;
    totalEarnings4Quarters += earn;
  });

  // E. Çıxılan Günlər (ÇTG) və Bölən Təqvim Günləri (BTG)
  const totalExcludedDays = excludedDaysList.reduce((sum, item) => sum + (Number(item.days) || 0), 0);
  const BTG = Math.max(1, quarterInfo.totalBaseDays - totalExcludedDays);

  // F. Bir Təqvim Gününə Düşən Orta Qazanc (OQM)
  let dailyRateOQM = 0;
  let isClause2_9Applied = false;

  if (totalEarnings4Quarters > 0) {
    dailyRateOQM = totalEarnings4Quarters / BTG;
  } else {
    // Qayda bənd 2.9: 4 rübdə qazanc olmadıqda minimum aylıq əməkhaqqı / xəstələndiyi ayın təqvim günləri
    isClause2_9Applied = true;
    const daysInEventMonth = new Date(startD.getFullYear(), startD.getMonth() + 1, 0).getDate();
    dailyRateOQM = minimumWage / daysInEventMonth;
  }

  dailyRateOQM = Number(dailyRateOQM.toFixed(4));

  // G. Xəstəlik günlərinin təqvim ayları üzrə bölünməsi
  const monthSplits = splitSickLeaveDaysByMonth(eventStartDate, eventEndDate);

  // H. Maliyyələşmə Bölgüsü (İlk 14 gün işəgötürən / 15-ci gündən DSMF)
  const alreadyUsedEmployerDays = isContinuation ? Math.min(14, Number(previouslyPaidEmployerDays) || 0) : 0;
  const remainingEmployerQuota = Math.max(0, STATUTORY_BASE_PARAMS.EMPLOYER_PAID_DAYS - alreadyUsedEmployerDays);

  const employerDays = Math.min(remainingEmployerQuota, TGS);
  const dsmfDays = Math.max(0, TGS - employerDays);

  // Qayda 2.17 üzrə xüsusi işəgötürən maliyyələşməsi (yalnız təqsir təsdiqləndikdə)
  const isSpecialEmployerFunding =
    caseType === 'OCCUPATIONAL_ACCIDENT_OR_DISEASE' && employerFaultStatus === 'CONFIRMED';

  // I. Aylıq Tavan Limitlərinin Hesablanması (Bənd 2.10.1 və 2.10.2)
  const totalMonthlyBaseSalary = monthlySalary + (isAdditionalJob ? Number(additionalJobMonthlySalary) || 0 : 0);
  const pensionCapMonthly = minimumPension * STATUTORY_BASE_PARAMS.PENSION_CAP_MULTIPLIER; // 25 * 320 = 8000 AZN
  const salaryCapMonthly = totalMonthlyBaseSalary * STATUTORY_BASE_PARAMS.SALARY_CAP_MULTIPLIER;

  // İstehsalat qəzasında 2x maaş limiti tətbiq edilmir, yalnız 25x pensiya limiti qalır
  const effectiveMonthlyCap = (caseType === 'OCCUPATIONAL_ACCIDENT_OR_DISEASE')
    ? pensionCapMonthly
    : Math.min(salaryCapMonthly, pensionCapMonthly);

  // J. Hər ay üzrə müavinət və tavan tətbiqi
  let totalGrossBenefit = 0;
  let totalEmployerGross = 0;
  let totalDsmfGross = 0;

  let accruedDays = 0;
  const monthlyBreakdown = monthSplits.map((m) => {
    const daysInThisMonth = m.sickDaysInMonth;
    const daysInCalendarMonth = m.totalCalendarDaysInMonth;

    // Bu ayın tavan gündəlik norması
    const maxDailyLimitInMonth = effectiveMonthlyCap / daysInCalendarMonth;
    const effectiveDailyRate = Math.min(dailyRateOQM, maxDailyLimitInMonth);

    // Ay üzrə işəgötürən və DSMF günləri
    let monthEmpDays = 0;
    let monthDsmfDays = 0;

    for (let day = 1; day <= daysInThisMonth; day++) {
      accruedDays += 1;
      if (accruedDays <= employerDays) {
        monthEmpDays += 1;
      } else {
        monthDsmfDays += 1;
      }
    }

    if (isSpecialEmployerFunding) {
      // 2.17 üzrə təqsirkar sığortaedən bütün günləri ödəyir
      monthEmpDays = daysInThisMonth;
      monthDsmfDays = 0;
    }

    const monthGross = Number((daysInThisMonth * effectiveDailyRate * eligibility.rate).toFixed(2));
    const empGross = Number((monthEmpDays * effectiveDailyRate * eligibility.rate).toFixed(2));
    const dsmfGross = Number((monthDsmfDays * effectiveDailyRate * eligibility.rate).toFixed(2));

    totalGrossBenefit += monthGross;
    totalEmployerGross += empGross;
    totalDsmfGross += dsmfGross;

    return {
      monthKey: m.monthKey,
      totalCalendarDaysInMonth: daysInCalendarMonth,
      sickDaysInMonth: daysInThisMonth,
      employerDays: monthEmpDays,
      dsmfDays: monthDsmfDays,
      effectiveDailyRate: Number(effectiveDailyRate.toFixed(4)),
      isCapApplied: dailyRateOQM > maxDailyLimitInMonth,
      monthGross,
      employerGross: empGross,
      dsmfGross: dsmfGross,
    };
  });

  totalGrossBenefit = Number(totalGrossBenefit.toFixed(2));
  totalEmployerGross = Number(totalEmployerGross.toFixed(2));
  totalDsmfGross = Number(totalDsmfGross.toFixed(2));

  // K. Layer 2: Payroll Tax Simulation (Mühasibat və Vergi Rekonsilyasiyası)
  let cumulativeTax = 0;
  const simulatedMonths = monthlyBreakdown.map((mb) => {
    const other = monthlyOtherIncomes[mb.monthKey] || {};
    const baseSalary = Number(other.salary) || 0;
    const vacationPay = Number(other.vacation) || 0;
    const sickBenefit = mb.monthGross;

    // Aylıq muzdlu gəlirlər cəmlənir (VM m.98 və 101.1-1)
    const totalMonthlyEmploymentIncome = Number((baseSalary + vacationPay + sickBenefit).toFixed(2));

    // 200 AZN güzəşt aylıq cəmi gəlirə yalnız 1 dəfə tətbiq olunur
    const monthlyIncomeTax = calculateIncomeTax2026(totalMonthlyEmploymentIncome, true);
    cumulativeTax += monthlyIncomeTax;

    // MDSS, İTS, İŞS yalnız maaş və məzuniyyətə tətbiq olunur, bülletendən tutulmur
    const salaryAndVacation = baseSalary + vacationPay;
    const mdss = salaryAndVacation > 200 ? Number((6 + (salaryAndVacation - 200) * 0.1).toFixed(2)) : Number((salaryAndVacation * 0.03).toFixed(2));
    const its = Number((salaryAndVacation * 0.02).toFixed(2));
    const iss = Number((salaryAndVacation * 0.005).toFixed(2));

    return {
      monthKey: mb.monthKey,
      baseSalary,
      vacationPay,
      sickBenefit,
      totalMonthlyEmploymentIncome,
      monthlyIncomeTax,
      mdssOnSalaryAndVacation: mdss,
      itsOnSalaryAndVacation: its,
      issOnSalaryAndVacation: iss,
      mdssOnSickBenefit: 0.0,
      itsOnSickBenefit: 0.0,
      issOnSickBenefit: 0.0,
    };
  });

  return {
    inputs: input,
    eligibility,
    TGS,
    isMaxPeriodExceeded,
    legalNotices,
    isClause2_9Applied,
    dailyRateOQM,
    BTG,
    totalEarnings4Quarters: Number(totalEarnings4Quarters.toFixed(2)),
    quarterInfo,
    effectiveMonthlyCap,
    primaryJobMonthlySalary: monthlySalary,
    additionalJobMonthlySalary: isAdditionalJob ? Number(additionalJobMonthlySalary) || 0 : 0,
    combinedMonthlySalary: totalMonthlyBaseSalary,
    // Qat 1: Qanuni Brüt Məbləğlər
    grossBenefit: totalGrossBenefit,
    employerShareGross: totalEmployerGross,
    dsmfShareGross: totalDsmfGross,
    employerDays,
    dsmfDays,
    isSpecialEmployerFunding,
    monthlyBreakdown,
    // Qat 2: Payroll Vergi Simulyasiyası
    taxSimulation: {
      totalEstimatedTax: Number(cumulativeTax.toFixed(2)),
      months: simulatedMonths,
      legalAdvisoryNotice: legalNotices.taxSimulationDisclaimer,
    },
  };
}

/**
 * 6. Test Paketi — Bütün 27 Test Keysi (TC-00 - TC-26)
 */
export function runSickLeaveCalculatorTests() {
  const testResults = [];

  const addTest = ({ id, testName, input, expected, actual, passed }) => {
    testResults.push({
      id,
      testName,
      input,
      expected,
      actual,
      status: passed ? 'PASSED' : 'FAILED',
    });
    if (!passed) {
      throw new Error(`TEST FAILED: ${id} - ${testName}. Expected ${expected}, got ${actual}`);
    }
  };

  // TC-00: Keçid Tarixi (30.04.2026 vs 01.05.2026)
  const tc00_legacy = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-04-30',
    eventEndDate: '2026-05-05',
    seniorityYears: 9,
  });
  addTest({
    id: 'TC-00',
    testName: 'Keçid Tarixi — Köhnə Rejim (80%)',
    input: 'eventStartDate: 2026-04-30, seniority: 9 il',
    expected: 'isLegacy=true, rate=0.8 (80%)',
    actual: `isLegacy=${tc00_legacy.eligibility.isLegacy}, rate=${tc00_legacy.eligibility.rate}`,
    passed: tc00_legacy.eligibility.isLegacy === true && tc00_legacy.eligibility.rate === 0.8,
  });

  const tc00_new = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-05-01',
    eventEndDate: '2026-05-05',
    seniorityYears: 9,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-00',
    testName: 'Keçid Tarixi — Yeni Rejim (100%)',
    input: 'eventStartDate: 2026-05-01, seniority: 9 il',
    expected: 'isLegacy=false, rate=1.0 (100%)',
    actual: `isLegacy=${tc00_new.eligibility.isLegacy}, rate=${tc00_new.eligibility.rate}`,
    passed: tc00_new.eligibility.isLegacy === false && tc00_new.eligibility.rate === 1.0,
  });

  // TC-01: Staj < 6 ay (5 ay 29 gün) -> NOT_ELIGIBLE, 0 AZN
  const tc01 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10',
    seniorityYears: 0,
    seniorityMonths: 5,
    seniorityDays: 29,
  });
  addTest({
    id: 'TC-01',
    testName: 'Staj 6 aydan az (5 ay 29 gün)',
    input: 'seniority: 0 il 5 ay 29 gün',
    expected: 'benefitStatus=NOT_ELIGIBLE, grossBenefit=0.00 AZN',
    actual: `benefitStatus=${tc01.eligibility.benefitStatus}, grossBenefit=${tc01.grossBenefit} AZN`,
    passed: tc01.eligibility.benefitStatus === 'NOT_ELIGIBLE' && tc01.grossBenefit === 0.0,
  });

  // TC-02: Dəqiq 6 ay staj -> ELIGIBLE, 100%
  const tc02 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10',
    seniorityYears: 0,
    seniorityMonths: 6,
    seniorityDays: 0,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-02',
    testName: 'Dəqiq 6 ay staj',
    input: 'seniority: 0 il 6 ay 0 gün',
    expected: 'benefitStatus=ELIGIBLE, rate=1.0 (100%)',
    actual: `benefitStatus=${tc02.eligibility.benefitStatus}, rate=${tc02.eligibility.rate}`,
    passed: tc02.eligibility.benefitStatus === 'ELIGIBLE' && tc02.eligibility.rate === 1.0,
  });

  // TC-03: Staj 25 il -> 100% (60/80 pillələri ləğv olunub)
  const tc03 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10',
    seniorityYears: 25,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-03',
    testName: 'Staj 25 il (Yeni qayda ilə 100%)',
    input: 'seniority: 25 il',
    expected: 'rate=1.0 (100%)',
    actual: `rate=${tc03.eligibility.rate}`,
    passed: tc03.eligibility.rate === 1.0,
  });

  // TC-04: 10 günlük xəstəlik -> 10 gün işəgötürən, 0 gün DSMF
  const tc04 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10', // 10 gün
    seniorityYears: 2,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-04',
    testName: '10 günlük bülleten (100% işəgötürən)',
    input: 'TGS: 10 gün',
    expected: 'employerDays=10, dsmfDays=0',
    actual: `employerDays=${tc04.employerDays}, dsmfDays=${tc04.dsmfDays}`,
    passed: tc04.employerDays === 10 && tc04.dsmfDays === 0,
  });

  // TC-05: Dəqiq 14 gün xəstəlik -> 14 gün işəgötürən, 0 gün DSMF
  const tc05 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-14', // 14 gün
    seniorityYears: 2,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-05',
    testName: 'Dəqiq 14 günlük bülleten (tam işəgötürən kvotası)',
    input: 'TGS: 14 gün',
    expected: 'employerDays=14, dsmfDays=0',
    actual: `employerDays=${tc05.employerDays}, dsmfDays=${tc05.dsmfDays}`,
    passed: tc05.employerDays === 14 && tc05.dsmfDays === 0,
  });

  // TC-06: 20 günlük xəstəlik -> 14 gün işəgötürən, 6 gün DSMF
  const tc06 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-20', // 20 gün
    seniorityYears: 2,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-06',
    testName: '20 günlük bülleten (14 işəgötürən / 6 DSMF)',
    input: 'TGS: 20 gün',
    expected: 'employerDays=14, dsmfDays=6',
    actual: `employerDays=${tc06.employerDays}, dsmfDays=${tc06.dsmfDays}`,
    passed: tc06.employerDays === 14 && tc06.dsmfDays === 6,
  });

  // TC-07: Ay sərhədi (25.09 - 10.10, 16 gün) -> 6 gün sentyabr + 8 gün oktyabr işəgötürən; 2 gün oktyabr DSMF
  const tc07 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-09-25',
    eventEndDate: '2026-10-10', // 16 gün
    seniorityYears: 2,
    quarterlyEarnings: { '2025_Q4': 3000, '2026_Q1': 3000, '2026_Q2': 3000, '2026_Q3': 3000 },
  });
  addTest({
    id: 'TC-07',
    testName: 'Ay sərhədini keçən dövr (14 gün limiti sıfırlanmır)',
    input: '25.09 - 10.10 (16 gün)',
    expected: 'employerDays=14 (6 Sentyabr + 8 Oktyabr), dsmfDays=2 (Oktyabr)',
    actual: `employerDays=${tc07.employerDays} (Sent: ${tc07.monthlyBreakdown[0].employerDays}, Okt: ${tc07.monthlyBreakdown[1].employerDays}), dsmfDays=${tc07.dsmfDays}`,
    passed: tc07.employerDays === 14 && tc07.dsmfDays === 2 && tc07.monthlyBreakdown[0].employerDays === 6 && tc07.monthlyBreakdown[1].employerDays === 8,
  });

  // TC-08: Fasiləsiz ardıcıl bülleten (əvvəl 10 gün işəgötürən ödənib) -> qalan 4 gün işəgötürən, qalanı DSMF
  const tc08 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-11',
    eventEndDate: '2026-06-20', // 10 gün
    seniorityYears: 2,
    isContinuation: true,
    previouslyPaidEmployerDays: 10,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
  });
  addTest({
    id: 'TC-08',
    testName: 'Fasiləsiz ardıcıl bülleten (əvvəlki günlər çıxılır)',
    input: 'isContinuation=true, previouslyPaid=10 gün, TGS=10 gün',
    expected: 'employerDays=4, dsmfDays=6',
    actual: `employerDays=${tc08.employerDays}, dsmfDays=${tc08.dsmfDays}`,
    passed: tc08.employerDays === 4 && tc08.dsmfDays === 6,
  });

  // TC-09: Fasiləli xəstəlik (yeni hadisə) -> 14 gün yenidən işəgötürən
  const tc09 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-07-01',
    eventEndDate: '2026-07-15', // 15 gün
    seniorityYears: 2,
    isContinuation: false,
    quarterlyEarnings: { '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000, '2026_Q2': 3000 },
  });
  addTest({
    id: 'TC-09',
    testName: 'Fasiləli xəstəlik (yeni hadisə 14 günlük kvotanı sıfırlayır)',
    input: 'isContinuation=false, TGS=15 gün',
    expected: 'employerDays=14, dsmfDays=1',
    actual: `employerDays=${tc09.employerDays}, dsmfDays=${tc09.dsmfDays}`,
    passed: tc09.employerDays === 14 && tc09.dsmfDays === 1,
  });

  // TC-10: Eyni ayda maaş + bülleten -> GV üçün cəmlənir, 200 AZN güzəşt 1 dəfə tətbiq olunur
  const tc10 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-05', // 5 gün
    seniorityYears: 2,
    monthlySalary: 1500,
    quarterlyEarnings: { '2025_Q2': 4500, '2025_Q3': 4500, '2025_Q4': 4500, '2026_Q1': 4500 },
    monthlyOtherIncomes: { '2026-06': { salary: 1200, vacation: 0 } },
  });
  addTest({
    id: 'TC-10',
    testName: 'Eyni ayda maaş + bülleten gəlir vergisi cəmlənməsi',
    input: 'salary: 1200, sickBenefit: ~246.58',
    expected: 'totalMonthlyEmploymentIncome > 1200, tax calculated once',
    actual: `totalIncome=${tc10.taxSimulation.months[0].totalMonthlyEmploymentIncome}, tax=${tc10.taxSimulation.months[0].monthlyIncomeTax}`,
    passed: tc10.taxSimulation.months[0].totalMonthlyEmploymentIncome > 1200 && tc10.taxSimulation.months[0].monthlyIncomeTax > 0,
  });

  // TC-11: Maaş + məzuniyyət + bülleten -> MDSS/İTS/İŞS bülletenə 0%
  const tc11 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-05',
    seniorityYears: 2,
    monthlySalary: 2000,
    quarterlyEarnings: { '2025_Q2': 6000, '2025_Q3': 6000, '2025_Q4': 6000, '2026_Q1': 6000 },
    monthlyOtherIncomes: { '2026-06': { salary: 1000, vacation: 500 } },
  });
  addTest({
    id: 'TC-11',
    testName: 'Bülletendən MDSS, İTS və İŞS tutulmaması (0%)',
    input: 'salary=1000, vacation=500, sickLeave=present',
    expected: 'mdssOnSick=0.0, itsOnSick=0.0, issOnSick=0.0',
    actual: `mdss=${tc11.taxSimulation.months[0].mdssOnSickBenefit}, its=${tc11.taxSimulation.months[0].itsOnSickBenefit}, iss=${tc11.taxSimulation.months[0].issOnSickBenefit}`,
    passed: tc11.taxSimulation.months[0].mdssOnSickBenefit === 0.0 && tc11.taxSimulation.months[0].itsOnSickBenefit === 0.0 && tc11.taxSimulation.months[0].issOnSickBenefit === 0.0,
  });

  // TC-12: Çoxaylıq vergi simulyasiyası (25.09 - 10.10)
  const tc12 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-09-25',
    eventEndDate: '2026-10-10',
    seniorityYears: 3,
    monthlySalary: 1500,
    quarterlyEarnings: { '2025_Q4': 4500, '2026_Q1': 4500, '2026_Q2': 4500, '2026_Q3': 4500 },
    monthlyOtherIncomes: {
      '2026-09': { salary: 1200, vacation: 0 },
      '2026-10': { salary: 1000, vacation: 0 },
    },
  });
  const m9 = tc12.taxSimulation.months[0];
  const m10 = tc12.taxSimulation.months[1];
  addTest({
    id: 'TC-12',
    testName: 'Çoxaylıq vergi simulyasiyası (Sentyabr və Oktyabr ayrılıqda)',
    input: '25.09-10.10, Sept salary: 1200, Oct salary: 1000',
    expected: 'Both months have separate tax bases and 200 AZN exemptions',
    actual: `Sept Tax: ${m9.monthlyIncomeTax}, Oct Tax: ${m10.monthlyIncomeTax}, Total: ${tc12.taxSimulation.totalEstimatedTax}`,
    passed: m9 && m10 && m9.monthKey === '2026-09' && m10.monthKey === '2026-10' && m9.monthlyIncomeTax > 0 && m10.monthlyIncomeTax > 0,
  });

  // TC-13: Çıxılan günlər (ÇTG) -> BTG-dən çıxılır
  const tc13 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10',
    seniorityYears: 2,
    quarterlyEarnings: { '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000 },
    excludedDaysList: [{ type: 'sick', days: 20 }],
  });
  addTest({
    id: 'TC-13',
    testName: 'Çıxılan günlər (BTG = ÜTG - ÇTG)',
    input: 'totalBaseDays=365, excludedDays=20',
    expected: 'BTG=345',
    actual: `BTG=${tc13.BTG}`,
    passed: tc13.BTG === tc13.quarterInfo.totalBaseDays - 20,
  });

  // TC-14: Staj çatışmazlığına görə ödənilməmiş xəstəlik günlərinin ÇTG-dən çıxılması
  const tc14 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-08-01',
    eventEndDate: '2026-08-10',
    seniorityYears: 1,
    quarterlyEarnings: { '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000, '2026_Q2': 3000 },
    excludedDaysList: [{ type: 'unpaid_sick_seniority', days: 12 }],
  });
  addTest({
    id: 'TC-14',
    testName: 'Staj çatışmazlığına görə ödənilməmiş bülleten günlərinin ÇTG kimi çıxılması',
    input: 'excludedDays: 12 gün ödənilməmiş bülleten',
    expected: 'BTG = totalBaseDays - 12',
    actual: `BTG=${tc14.BTG}`,
    passed: tc14.BTG === tc14.quarterInfo.totalBaseDays - 12,
  });

  // TC-15: Hamiləlik və doğuş dövrünün (dekret) ÇTG-dən çıxılması
  const tc15 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-08-01',
    eventEndDate: '2026-08-10',
    seniorityYears: 3,
    quarterlyEarnings: { '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000, '2026_Q2': 3000 },
    excludedDaysList: [{ type: 'maternity_leave', days: 126 }],
  });
  addTest({
    id: 'TC-15',
    testName: 'Hamiləlik və doğuş (dekret) dövrünün ÇTG kimi çıxılması',
    input: 'excludedDays: 126 gün dekret',
    expected: 'BTG = totalBaseDays - 126',
    actual: `BTG=${tc15.BTG}`,
    passed: tc15.BTG === tc15.quarterInfo.totalBaseDays - 126,
  });

  // TC-16: Qismən ödənişli sosial məzuniyyətin (uşağa qulluq) ÇTG-dən çıxılması
  const tc16 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-08-01',
    eventEndDate: '2026-08-10',
    seniorityYears: 3,
    quarterlyEarnings: { '2025_Q3': 3000, '2025_Q4': 3000, '2026_Q1': 3000, '2026_Q2': 3000 },
    excludedDaysList: [{ type: 'childcare_leave', days: 90 }],
  });
  addTest({
    id: 'TC-16',
    testName: 'Uşağa qulluq məzuniyyəti günlərinin ÇTG kimi çıxılması',
    input: 'excludedDays: 90 gün uşağa qulluq',
    expected: 'BTG = totalBaseDays - 90',
    actual: `BTG=${tc16.BTG}`,
    passed: tc16.BTG === tc16.quarterInfo.totalBaseDays - 90,
  });

  // TC-17: İstehsalat qəzası (OCCUPATIONAL_ACCIDENT_OR_DISEASE) -> 2x maaş limiti tətbiq olunmur, 25x pensiya qalır
  const tc17 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10',
    seniorityYears: 2,
    caseType: 'OCCUPATIONAL_ACCIDENT_OR_DISEASE',
    employerFaultStatus: 'CONFIRMED',
    monthlySalary: 1000,
    quarterlyEarnings: { '2025_Q2': 15000, '2025_Q3': 15000, '2025_Q4': 15000, '2026_Q1': 15000 },
  });
  addTest({
    id: 'TC-17',
    testName: 'İstehsalat qəzasında 2x maaş limiti ləğvi, 25x pensiya tavanı qalması',
    input: 'monthlySalary=1000, 2x=2000, 25x pensiya=8000',
    expected: 'effectiveMonthlyCap = 8000 (not capped at 2000)',
    actual: `effectiveMonthlyCap=${tc17.effectiveMonthlyCap}`,
    passed: tc17.effectiveMonthlyCap === 8000,
  });

  // TC-18: Əsas və əlavə iş yeri
  const tc18 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-20', // 20 gün (14 işəgötürən, 6 DSMF)
    seniorityYears: 3,
    monthlySalary: 1500,
    isAdditionalJob: true,
    additionalJobMonthlySalary: 500,
    quarterlyEarnings: { '2025_Q2': 6000, '2025_Q3': 6000, '2025_Q4': 6000, '2026_Q1': 6000 },
  });
  addTest({
    id: 'TC-18',
    testName: 'Əsas və əlavə iş yeri (2 misli maaş tavanında cəmlənmə)',
    input: 'primarySalary=1500, additionalSalary=500 -> total=2000',
    expected: 'effectiveMonthlyCap = 4000 (2.0 * 2000)',
    actual: `combinedSalary=${tc18.combinedMonthlySalary}, cap=${tc18.effectiveMonthlyCap}`,
    passed: tc18.combinedMonthlySalary === 2000 && tc18.effectiveMonthlyCap === 4000,
  });

  // TC-19: 25x Minimum Pensiya Tavanı ($25 * 320 = 8 000 AZN$)
  const tc19 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-30', // 30 gün
    seniorityYears: 2,
    monthlySalary: 10000,
    quarterlyEarnings: { '2025_Q2': 60000, '2025_Q3': 60000, '2025_Q4': 60000, '2026_Q1': 60000 },
  });
  addTest({
    id: 'TC-19',
    testName: '25 misli minimum pensiya tavanı (8 000 AZN)',
    input: 'extremely high earnings (quarterly: 60 000 AZN)',
    expected: 'grossBenefit <= 8000.01',
    actual: `grossBenefit=${tc19.grossBenefit} AZN`,
    passed: tc19.grossBenefit <= 8000.01,
  });

  // TC-20: 2x Vəzifə Maaşı Tavanı (Standart halda)
  const tc20 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-30',
    seniorityYears: 2,
    caseType: 'ORDINARY_ILLNESS',
    monthlySalary: 1000, // 2x = 2000 AZN
    quarterlyEarnings: { '2025_Q2': 30000, '2025_Q3': 30000, '2025_Q4': 30000, '2026_Q1': 30000 },
  });
  addTest({
    id: 'TC-20',
    testName: '2 misli vəzifə maaşı tavanı (Standart xəstəlik)',
    input: 'monthlySalary=1000, high earnings',
    expected: 'grossBenefit <= 2000.01',
    actual: `grossBenefit=${tc20.grossBenefit} AZN`,
    passed: tc20.grossBenefit <= 2000.01,
  });

  // TC-21: ƏM 131 hüquqi xəbərdarlığı
  const tc21 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-07-01',
    eventEndDate: '2026-07-10',
    seniorityYears: 2,
  });
  addTest({
    id: 'TC-21',
    testName: 'Əmək Məcəlləsinin 131-ci maddəsi üzrə məzuniyyətin uzadılması hüququ bildirişi',
    input: 'eventStartDate: 2026-07-01',
    expected: 'legalNotices.lcArticle131Notice present',
    actual: tc21.legalNotices?.lcArticle131Notice ? 'Mövcuddur' : 'Yoxdur',
    passed: !!tc21.legalNotices?.lcArticle131Notice && tc21.legalNotices.lcArticle131Notice.includes('131'),
  });

  // TC-22: DSMF hissəsinin payroll tax simulation
  const tc22 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-25', // 25 gün (14 işəgötürən, 11 DSMF)
    seniorityYears: 2,
    monthlySalary: 1500,
    quarterlyEarnings: { '2025_Q2': 4500, '2025_Q3': 4500, '2025_Q4': 4500, '2026_Q1': 4500 },
  });
  addTest({
    id: 'TC-22',
    testName: 'DSMF hissəsinin payroll vergi simulyasiyası (VM 102.1.4)',
    input: 'TGS: 25 gün, DSMF: 11 gün',
    expected: 'dsmfDays > 0, totalEstimatedTax > 0, MDSS on benefit = 0%',
    actual: `dsmfDays=${tc22.dsmfDays}, estTax=${tc22.taxSimulation.totalEstimatedTax}`,
    passed: tc22.dsmfDays === 11 && tc22.taxSimulation.totalEstimatedTax > 0 && tc22.taxSimulation.months[0].mdssOnSickBenefit === 0.0,
  });

  // TC-23: Bənd 2.9 (4 rübdə qazanc olmadıqda minimum əməkhaqqı fallback)
  const tc23 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10', // İyun 30 gündür
    seniorityYears: 2,
    quarterlyEarnings: { '2025_Q2': 0, '2025_Q3': 0, '2025_Q4': 0, '2026_Q1': 0 },
    minimumWage: 400,
  });
  addTest({
    id: 'TC-23',
    testName: 'Qayda bənd 2.9 (4 rübdə qazanc olmadıqda minimum əməkhaqqı fallback)',
    input: 'quarterlyEarnings=0, minimumWage=400, monthDays=30',
    expected: 'isClause2_9Applied=true, dailyRateOQM=13.3333',
    actual: `isClause2_9Applied=${tc23.isClause2_9Applied}, dailyRateOQM=${tc23.dailyRateOQM}`,
    passed: tc23.isClause2_9Applied === true && tc23.dailyRateOQM === Number((400 / 30).toFixed(4)),
  });

  // TC-24: Elektron xəstəlik vərəqəsi (MEİS) bildirişi
  const tc24 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-06-01',
    eventEndDate: '2026-06-10',
    seniorityYears: 2,
  });
  addTest({
    id: 'TC-24',
    testName: 'Elektron xəstəlik vərəqəsi (MEİS / e-bülleten) bildirişi',
    input: 'standard check',
    expected: 'legalNotices.electronicSickLeaveNotice present with MEİS reference',
    actual: tc24.legalNotices?.electronicSickLeaveNotice ? 'Mövcuddur' : 'Yoxdur',
    passed: !!tc24.legalNotices?.electronicSickLeaveNotice && tc24.legalNotices.electronicSickLeaveNotice.includes('MEİS'),
  });

  // TC-25: Four Quarter Boundary Test
  const qTest1 = getPrecedingFourQuarters('2026-05-15'); // May -> Q2. Preceding: 2025-Q2, 2025-Q3, 2025-Q4, 2026-Q1
  const qTest2 = getPrecedingFourQuarters('2026-07-01'); // July -> Q3. Preceding: 2025-Q3, 2025-Q4, 2026-Q1, 2026-Q2
  const qTest3 = getPrecedingFourQuarters('2026-09-30'); // Sept -> Q3. Preceding: 2025-Q3, 2025-Q4, 2026-Q1, 2026-Q2
  const qTest4 = getPrecedingFourQuarters('2026-10-01'); // Oct -> Q4. Preceding: 2025-Q4, 2026-Q1, 2026-Q2, 2026-Q3
  const isBoundaryCorrect =
    qTest1.quarters[0].key === '2025_Q2' && qTest1.quarters[3].key === '2026_Q1' &&
    qTest2.quarters[0].key === '2025_Q3' && qTest2.quarters[3].key === '2026_Q2' &&
    qTest3.quarters[0].key === '2025_Q3' && qTest3.quarters[3].key === '2026_Q2' &&
    qTest4.quarters[0].key === '2025_Q4' && qTest4.quarters[3].key === '2026_Q3';

  addTest({
    id: 'TC-25',
    testName: 'Hadisə tarixindən asılı olaraq əvvəlki 4 tam təqvim rübünün düzgün təyini (Boundary Test)',
    input: 'dates: 15.05.2026, 01.07.2026, 30.09.2026, 01.10.2026',
    expected: 'All 4 quarters match exact calendar boundaries',
    actual: isBoundaryCorrect ? 'Tam uyğundur' : 'Xəta aşkarlandı',
    passed: isBoundaryCorrect,
  });

  // TC-26: Maksimum 1 il (365 təqvim günü) qaydası
  const tc26 = calculateSickLeaveBenefit2026({
    eventStartDate: '2026-01-01',
    eventEndDate: '2027-01-10', // 375 gün
    seniorityYears: 5,
    quarterlyEarnings: { '2025_Q1': 3000, '2025_Q2': 3000, '2025_Q3': 3000, '2025_Q4': 3000 },
  });
  addTest({
    id: 'TC-26',
    testName: 'Müavinət müddətinin maksimum 1 il (365 gün) həddinin yoxlanılması (Qayda bənd 1.4)',
    input: 'TGS: 375 gün (> 365 gün)',
    expected: 'isMaxPeriodExceeded=true, maxPeriodWarning present',
    actual: `isMaxPeriodExceeded=${tc26.isMaxPeriodExceeded}, warning=${!!tc26.legalNotices?.maxPeriodWarning}`,
    passed: tc26.isMaxPeriodExceeded === true && !!tc26.legalNotices?.maxPeriodWarning,
  });

  return {
    passed: testResults.filter(t => t.status === 'PASSED').length,
    total: testResults.length,
    details: testResults,
  };
}
